import {
    Alert,
    Box,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { Tab, Tabs } from '@mui/material';
import { useState } from 'react';
import { PastHangouts } from '../components/PastHangouts';
import { useAllTime, useCurrentHangout, useStandings } from '../hooks/useHangout';
import { formatTokens } from '../lib/format';
import { getErrorMessage } from '../services/api-client';
import { colors, mono } from '../themes/theme';

export function Standings() {
    const [tab, setTab] = useState(0);
    const hangout = useCurrentHangout();
    const standings = useStandings(hangout.data?.id);
    const allTime = useAllTime();


    if (standings.isError) {
        return <Alert severity="error">{getErrorMessage(standings.error)}</Alert>;
    }

    return (
        <Stack spacing={2}>
            <Box>
                <Typography variant="overline" sx={{ color: colors.muted }}>
                    {tab === 0
                        ? (hangout.data?.name ?? 'Nothing running')
                        : tab === 1
                          ? 'How they finished'
                          : 'Every hangout so far'}
                </Typography>
                <Typography variant="h2">Standings</Typography>
            </Box>

            <Tabs
                value={tab}
                onChange={(_event, value: number) => setTab(value)}
                sx={{ borderBottom: `1px solid ${colors.line}`, minHeight: 40 }}
            >
                <Tab label="Tonight" />
                <Tab label="Past nights" />
                <Tab label="All time" />
            </Tabs>

            {tab === 1 && <PastHangouts />}

            {tab === 2 && (
                <Paper>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Player</TableCell>
                                <TableCell align="right">Won</TableCell>
                                <TableCell align="right">Played</TableCell>
                                <TableCell align="right">ROI</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {(allTime.data ?? []).map((row) => (
                                <TableRow key={row.userId}>
                                    <TableCell>{row.handle}</TableCell>
                                    <TableCell align="right" sx={{ fontFamily: mono, fontVariantNumeric: 'tabular-nums', color: colors.safety }}>
                                        {row.wins}
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontFamily: mono, fontVariantNumeric: 'tabular-nums' }}>
                                        {row.played}
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontFamily: mono, fontVariantNumeric: 'tabular-nums', color: row.roi >= 0 ? colors.win : colors.loss }}>
                                        {row.roi >= 0 ? '+' : ''}{(row.roi * 100).toFixed(0)}%
                                    </TableCell>
                                </TableRow>
                            ))}
                            {(allTime.data ?? []).length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={4} sx={{ color: colors.muted }}>
                                        Nothing here until a hangout has been closed out.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </Paper>
            )}

            {tab === 0 && !hangout.data && (
                <Typography color="text.secondary">No hangout is running.</Typography>
            )}

            {tab === 0 && hangout.data && <Paper>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ width: 44 }}>#</TableCell>
                            <TableCell>Player</TableCell>
                            <TableCell align="right">Tokens</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {standings.data?.map((row, index) => (
                            <TableRow key={row.userId}>
                                <TableCell
                                    sx={{
                                        fontFamily: mono,
                                        color: colors.muted,
                                        fontVariantNumeric: 'tabular-nums',
                                    }}
                                >
                                    {index + 1}
                                </TableCell>
                                <TableCell
                                    sx={{
                                        color: row.isLive ? colors.ink : colors.muted,
                                        textDecoration: row.isLive ? 'none' : 'line-through',
                                    }}
                                >
                                    {row.handle}
                                    {!row.isLive && (
                                        <Typography
                                            component="span"
                                            variant="overline"
                                            sx={{ ml: 1, color: colors.loss }}
                                        >
                                            out
                                        </Typography>
                                    )}
                                </TableCell>
                                <TableCell
                                    align="right"
                                    sx={{
                                        fontFamily: mono,
                                        fontVariantNumeric: 'tabular-nums',
                                        color: row.isLive ? colors.ink : colors.muted,
                                    }}
                                >
                                    {formatTokens(row.balance)}
                                </TableCell>
                            </TableRow>
                        ))}
                        {standings.data?.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={3} sx={{ color: colors.muted }}>
                                    Nobody has joined yet.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </Paper>}
        </Stack>
    );
}
